import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import getDashboardMenu from '../components/SideNavbar/DashboardMenu';
import getStaffDashboardMenu from '../components/SideNavbar/StaffDashboardMenu';
import { FormattedMessage } from '@edx/frontend-platform/i18n';

const HeaderTitleContext = createContext();

/**
 * Provider component for header title functionality
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} Context provider with children
 */
export function HeaderTitleProvider({ children }) {
    const [title, setTitle] = useState('');
    const pathname = useLocation().pathname;

    const { DashboardMenu, DashboardFooter, ProfileMenu } = getDashboardMenu();
    const { StaffDashboardMenu, StaffDashboardFooter, StaffProfileMenu } = getStaffDashboardMenu();
    useEffect(() => {

        // Find parent and child items for nested routes
        let parentTitle = '';
        let childTitle = '';

        // Helper function to find matches in a menu array
        const findMatch = (menuItems) => {
            // Try exact match first
            const exactMatch = menuItems?.find(item => item.href?.includes(pathname));
            if (exactMatch) {
                return { exactMatch };
            }

            // Look for parent/child relationship
            let foundParent = '';
            let foundChild = '';
            menuItems?.forEach(item => {
                if (item.children) {
                    const matchingChild = item.children.find(child =>
                        child.href?.includes(pathname)
                    );

                    if (matchingChild) {
                        foundParent = <FormattedMessage {...item.text.props} />;
                        foundChild = <FormattedMessage {...matchingChild.text.props} />;
                    }
                }
            });

            return { parentTitle: foundParent, childTitle: foundChild };
        };

        // Check all menus in order of priority
        const allMenus = [StaffDashboardMenu, DashboardMenu, StaffDashboardFooter, StaffProfileMenu, DashboardFooter, ProfileMenu];

        for (const menu of allMenus) {
            const { exactMatch, parentTitle: foundParent, childTitle: foundChild } = findMatch(menu);

            if (exactMatch) {
                setTitle(<FormattedMessage {...exactMatch.text.props} />);
                return;
            }

            if (foundParent && foundChild) {
                parentTitle = foundParent;
                childTitle = foundChild;
                break;
            }
        }

        // Set title if parent/child relationship was found
        if (parentTitle && childTitle) {
            setTitle(
                <>
                    {parentTitle}
                    {' / '}
                    {childTitle}
                </>
            );
        }
    }, [pathname]);


    return (
        <HeaderTitleContext.Provider value={{ title, setTitle }}>
            {children}
        </HeaderTitleContext.Provider>
    );
}

/**
 * Hook to access and manage header title state
 * @returns {Object} Header title context
 * @property {React.ReactNode} title - The current header title (may contain formatted messages)
 * @property {Function} setTitle - Function to update the header title
 */
export function useHeaderTitle() {
    return useContext(HeaderTitleContext);
}

export default useHeaderTitle;
